import React, { useEffect, useState } from 'react';
import { BoxStyled } from '../faisyEquation/style';
import { NavigationButtons } from '../../../shared/components/navigationButtons';
import { Box, Divider, Modal } from '@mui/material';
import { TextWithInfoIcon } from '../../../shared/components/textWithInfoIcon';
import InfoIcon from '../../../assets/icons/infoIcon.svg';
import { ArcContainer } from '../../../shared/components/arcContainer';
import { DoubleTextBlueCardStyled } from '../../../shared/components/doubleTextBlueCardStyled';
import { SingleNumberCard } from '../../../shared/components/numberInput/numberCards/singleNumberCard';
import ImproveTreatment from '../../../assets/icons/improveTreatment.svg';
import dataMonitorIcon from '../../../assets/icons/dataMonitorIcon.svg';
import { AddOnInfoModal } from '../../../shared/components/modal/addOnInfoModal';

import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../store/hooks';
import { metaDataSelector } from '../../../store/globalSelectors';
import { setModifiedPennSelection } from '../../../store/slices/metaDataSlice';
import { findModifiedPennRMR } from '../../../shared/utils/utilFunctions';

export const ModifiedPennState = () => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const { estimatingRequirements, patientInformation } =
    useAppSelector(metaDataSelector);
  const { modifiedPennState } = estimatingRequirements;
  const { metric, age, gender } = patientInformation;
  const { weight, estimatedDryWeight, height } = metric;
  const dispatch = useDispatch();
  const [rmr, setRMR] = useState(modifiedPennState.rmr);

  useEffect(() => {
    const consideredWeight =
      estimatedDryWeight > 0 ? estimatedDryWeight : weight;
    let calculatedRMR = 0;
    // const consideredWeight = 10,
    //   height = 10,
    //   age = 50;
    if (consideredWeight > 0 && height) {
      calculatedRMR = findModifiedPennRMR(
        gender.toLowerCase(),
        age.years,
        modifiedPennState.ve,
        modifiedPennState.tMax,
        consideredWeight,
        height
      );
    }
    dispatch(
      setModifiedPennSelection({
        ...modifiedPennState,
        rmr: calculatedRMR
      })
    );
    setRMR(calculatedRMR);
  }, [modifiedPennState.ve, modifiedPennState.tMax]);

  return (
    <>
      <BoxStyled>
        {/* Top Bar (title + info icon) */}
        <TextWithInfoIcon
          data-testid={'ModifiedPennInfoPopupButton'}
          title={'MODIFIED PENN STATE'}
          icon={InfoIcon}
          fontSize={'1.5rem'}
          lineHeight={'1.375rem'}
          variant={'h1'}
          handleIconClick={() => handleOpen()}
        />
        <p style={{ padding: '0.625rem 0px 1.875rem 0px' }}>
          For ≥60 year olds with BMI ≥30kg/m
          <sup style={{ fontSize: '0.6em', verticalAlign: 'super' }}>2</sup>
        </p>
        <ArcContainer icon={dataMonitorIcon}>
          <Box sx={{ textAlign: 'center', mb: 2, mt: 5 }}>
            <Box
              sx={{
                border: '0.0625rem solid #D9D9D6',
                borderRadius: '0.9375rem',
                marginTop: '1.25rem'
              }}
              className="pt-16"
            >
              <SingleNumberCard
                data-testid={'test-VE'}
                width={'100%'}
                title={'VE'}
                marginVal={'0.625rem'}
                range={{ min: 0, max: 250 }}
                subHead={'(L/min)'}
                incrementDecrementVal={0.1}
                value={modifiedPennState.ve}
                setValue={(val: number) =>
                  dispatch(
                    setModifiedPennSelection({
                      ...modifiedPennState,
                      ve: Number(val)
                    })
                  )
                }
              />
            </Box>
            <Divider
              sx={{
                backgroundColor: 'rgba(255,255,255,0.4)',
                my: 2,
                width: '100%',
                mx: 'auto'
              }}
            />
            <Box
              sx={{
                border: '0.0625rem solid #D9D9D6',
                borderRadius: '0.9375rem',
                marginTop: '1.25rem'
              }}
            >
              <SingleNumberCard
                width={'100%'}
                title={'TMAX'}
                marginVal={'0.625rem'}
                range={{ min: 0, max: 250 }}
                subHead={'(°C)'}
                incrementDecrementVal={0.1}
                value={modifiedPennState.tMax}
                setValue={(val: number) =>
                  dispatch(
                    setModifiedPennSelection({
                      ...modifiedPennState,
                      tMax: Number(val)
                    })
                  )
                }
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 5 }}>
            <DoubleTextBlueCardStyled
              heading1="RMR"
              label1={`${rmr} kcal`}
              icon={ImproveTreatment}
            />
          </Box>
        </ArcContainer>
      </BoxStyled>

      <NavigationButtons
        nextDisabled={false}
        nextRoute={
          patientInformation.type === 'PAEDIATRIC'
            ? '/estimating-requirements/paediatric-energy'
            : '/estimating-requirements/protein'
        }
      />
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <AddOnInfoModal
          heading1={`VE`}
          description1={` = Minute ventilation, litres per minute`}
          heading2={`TMAX`}
          description2={` = Maximum body temperature (in degrees Celcius) from previous 24 hours, or most recent body temperature`}
          heading3={`BMI`}
          description3={` = Body Mass Index`}
          heading4={`RMR`}
          description4={` = Resting Metabolic Rate`}
          title={`Reference:`}
          content={`Irish Nutrition and Dietetic Institute (2022) Nutrition Support
        Reference Guide.`}
          onClose={handleClose}
        />
      </Modal>
    </>
  );
};
